import { io, type Socket } from "socket.io-client";

import type { Session } from "./session";

/** Where the API's Admin signals arrive (docs/specs/reporting.md, ADR-0022). */
export const REALTIME_PATH = "/v1/realtime";

/**
 * Signals from the API ("something changed, fetch again"), never data. Connects on first use with the session's
 * access token (read again on every reconnect, so a refreshed token is picked up); a missed signal only waits for
 * the page's own polling.
 */
export interface Realtime {
  /** Calls `handler` whenever `event` arrives; returns the unsubscribe. */
  on(event: string, handler: () => void): () => void;
  close(): void;
}

export function createRealtime(baseUrl: string, session: Session): Realtime {
  let socket: Socket | undefined;
  const handlers = new Map<string, Set<() => void>>();
  const connect = (): void => {
    if (socket !== undefined) return;
    socket = io(`${baseUrl}/admin`, {
      path: REALTIME_PATH,
      transports: ["websocket"],
      auth: (send) => {
        send({ token: session.token() ?? "" });
      },
    });
    socket.onAny((event: string) => {
      for (const handler of handlers.get(event) ?? []) handler();
    });
  };
  return {
    on(event, handler) {
      connect();
      let set = handlers.get(event);
      if (!set) handlers.set(event, (set = new Set()));
      set.add(handler);
      return () => {
        set.delete(handler);
      };
    },
    close() {
      socket?.disconnect();
      socket = undefined;
    },
  };
}

/** For tests and pages without a server: nothing ever arrives. */
export const noRealtime: Realtime = { on: () => () => undefined, close: () => undefined };
